// src/auth/AuthContext.jsx
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { API_BASE_URL } from "../config";

const AuthContext = createContext(null);

// —— Config ——
const AUTH_KEY = "auth";
const AUTH_VERSION = "2";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(AUTH_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.version === AUTH_VERSION && parsed?.user) {
          setUser(parsed.user);
          setToken(parsed.token ?? null);
        } else {
          localStorage.removeItem(AUTH_KEY);
        }
      }
    } catch {
      localStorage.removeItem(AUTH_KEY);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      setReady(true);
      return;
    }

    const ctrl = new AbortController();
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/users/me`, {
          method: "GET",
          credentials: "include",
          signal: ctrl.signal,
        });

        if (!res.ok) throw new Error("invalid");
        setReady(true);
      } catch {
        setUser(null);
        setToken(null);
        localStorage.removeItem(AUTH_KEY);
        setReady(true);
      }
    })();

    return () => ctrl.abort();
  }, [user]);

  useEffect(() => {
    if (!user) {
      localStorage.removeItem(AUTH_KEY);
      return;
    }
    localStorage.setItem(
      AUTH_KEY,
      JSON.stringify({ version: AUTH_VERSION, user, token })
    );
  }, [user, token]);

  const login = (nextUser, nextToken = null) => {
    setUser(nextUser ?? null);
    setToken(nextToken ?? null);
  };

  const logout = async () => {
    try {
      await fetch(`${API_BASE_URL}/api/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch {
      // ignore
    } finally {
      setUser(null);
      setToken(null);
      localStorage.removeItem(AUTH_KEY);
    }
  };

  const value = useMemo(
    () => ({
      user,
      token,
      isAuthenticated: !!user,
      login,
      logout,
    }),
    [user, token]
  );

  if (!ready) return null;

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
